import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, ArrowLeft, Home, Compass, CheckCircle2, ShieldCheck, Sparkles, MapPin, Bookmark, Lightbulb } from "lucide-react";

const SITUATIONS = ["Employed full-time", "Working a side hustle", "Between jobs", "Already started a business"];
const TIMELINES = ["As soon as possible", "In 3–6 months", "In 6–12 months", "Still exploring"];
const BUDGETS = ["Under $1,000", "$1,000–$5,000", "$5,000–$15,000", "$15,000+", "Not sure yet"];

function Chips({ options, value, onChange, testid }) {
    return (
        <div className="flex flex-wrap gap-2" data-testid={testid}>
            {options.map((o) => (
                <button key={o} type="button" onClick={() => onChange(o)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${value === o ? "border-ocean bg-ocean text-white" : "border-slate-300 bg-white text-slate-700 hover:border-ocean/50"}`}>
                    {o}
                </button>
            ))}
        </div>
    );
}

export default function Strategizer() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [saved, setSaved] = useState(false);
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({ vision: "", situation: "", idea: "", timeline: "", budget: "" });
    const [busy, setBusy] = useState(false);
    const [roadmap, setRoadmap] = useState(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const [lead, setLead] = useState({ name: "", email: "" });
    const [leadBusy, setLeadBusy] = useState(false);
    const [leadDone, setLeadDone] = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/strategizer/roadmap", form);
            setRoadmap(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not generate roadmap. Please try again.");
        } finally { setBusy(false); }
    };

    const savePlan = async () => {
        if (!user) { toast.info("Sign in to save your roadmap to your portal."); navigate("/"); return; }
        try {
            await api.post("/me/roadmap", { roadmap, answers: form });
            setSaved(true);
            toast.success("Roadmap saved to your portal");
        } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save. Please try again."); }
    };

    const submitLead = async (e) => {
        e.preventDefault();
        setLeadBusy(true);
        try {
            await api.post("/inquiries", {
                type: "strategizer", name: lead.name, email: lead.email,
                product: "Business Launch Strategizer — full plan",
                message: `Vision: ${form.vision}\nSituation: ${form.situation}\nIdea: ${form.idea}\nTimeline: ${form.timeline}\nBudget: ${form.budget}`,
            });
            setLeadDone(true);
            toast.success("Request received");
        } catch (e2) { toast.error(apiError(e2.response?.data?.detail) || "Please try again."); }
        finally { setLeadBusy(false); }
    };

    const steps = [
        { title: "What's your vision?", render: (
            <div className="space-y-1.5">
                <Label htmlFor="vision">In your own words, what do you want to build or become?</Label>
                <Textarea id="vision" data-testid="strat-vision" rows={4} value={form.vision} onChange={(e) => set("vision", e.target.value)}
                    placeholder="e.g. I want to leave my 9–5 and run my own business so I can build wealth and own my time." />
                <p className="text-xs text-slate-500">There are no wrong answers — this guides your roadmap.</p>
            </div>
        )},
        { title: "Where are you right now?", render: (
            <Chips testid="strat-situation" options={SITUATIONS} value={form.situation} onChange={(v) => set("situation", v)} />
        )},
        { title: "Do you have a business idea?", render: (
            <div className="space-y-5">
                <div className="space-y-1.5">
                    <Label htmlFor="idea">Tell us about it (optional — "still exploring" is fine)</Label>
                    <Textarea id="idea" data-testid="strat-idea" rows={3} value={form.idea} onChange={(e) => set("idea", e.target.value)}
                        placeholder="e.g. A mobile detailing service / an online store / a consulting practice…" />
                </div>
                <div className="space-y-2">
                    <Label>When do you want to start?</Label>
                    <Chips testid="strat-timeline" options={TIMELINES} value={form.timeline} onChange={(v) => set("timeline", v)} />
                </div>
            </div>
        )},
        { title: "What can you invest to start?", render: (
            <Chips testid="strat-budget" options={BUDGETS} value={form.budget} onChange={(v) => set("budget", v)} />
        )},
    ];

    // ---------- Roadmap view ----------
    if (roadmap) {
        return (
            <div className="min-h-screen bg-pale" data-testid="strategizer-roadmap">
                <TopBar navigate={navigate} />
                <div className="mx-auto max-w-4xl px-4 py-10 lg:px-8">
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-teal/15 px-4 py-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-teal" /><span className="text-xs font-bold uppercase tracking-wide text-teal">Your personalized roadmap</span>
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-navy sm:text-4xl">Your Business Launch Roadmap</h1>
                    <p className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 text-base leading-relaxed text-slate-700" data-testid="roadmap-vision">{roadmap.vision_summary}</p>

                    <div className="mt-4">
                        <Button onClick={savePlan} disabled={saved} data-testid="roadmap-save-btn" variant="outline" className="border-ocean/40 font-semibold text-ocean hover:bg-ocean/5">
                            <Bookmark className={`mr-2 h-4 w-4 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved to My Plan" : "Save to my portal"}
                        </Button>
                    </div>

                    {Array.isArray(roadmap.recommended_structures) && roadmap.recommended_structures.length > 0 && (
                        <div className="mt-6 rounded-2xl border border-teal/30 bg-teal/5 p-5" data-testid="roadmap-recommended">
                            <div className="flex items-center gap-2 text-navy"><Lightbulb className="h-5 w-5 text-teal" /><h2 className="font-heading text-base font-bold">Structures worth asking about</h2></div>
                            <p className="mt-1 text-sm text-slate-600">Based on your answers — confirm the right fit with a licensed attorney/CPA.</p>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                {roadmap.recommended_structures.map((r, i) => (
                                    <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`recommended-${i}`}>
                                        <div className="font-heading text-sm font-bold text-navy">{r.name}</div>
                                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.why}</p>
                                        {r.id && <button onClick={() => navigate(`/compare?focus=${r.id}`)} data-testid={`recommended-link-${i}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-ocean hover:underline">See full comparison <ArrowRight className="h-4 w-4" /></button>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-start gap-3 rounded-2xl border border-ocean/20 bg-ocean/5 p-5">
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-ocean" />
                        <div><div className="font-heading text-sm font-bold text-navy">Is this for you?</div><p className="mt-1 text-sm leading-relaxed text-slate-700">{roadmap.readiness_check}</p></div>
                    </div>

                    <div className="mt-8 space-y-4">
                        {(roadmap.steps || []).map((s, i) => (
                            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`roadmap-step-${i}`}>
                                <div className="flex items-start gap-4">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy font-heading text-sm font-bold text-white">{i + 1}</div>
                                    <div className="flex-1">
                                        <h3 className="font-heading text-lg font-bold text-navy">{s.title}</h3>
                                        {s.why && <p className="mt-1 text-sm italic text-slate-500">{s.why}</p>}
                                        <ul className="mt-3 space-y-2">
                                            {(s.actions || []).map((a, j) => (
                                                <li key={j} className="flex items-start gap-2.5 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal" /> {a}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {roadmap.entity_note && (
                        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                            <div className="font-heading text-sm font-bold text-navy">Entity choice — quick education</div>
                            <p className="mt-1 text-sm leading-relaxed text-slate-700">{roadmap.entity_note}</p>
                            <button onClick={() => navigate("/compare")} data-testid="roadmap-compare-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ocean hover:underline">
                                Compare all entities & trusts side by side <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    {/* Paid CTA / lead capture */}
                    <div className="mt-10 rounded-[1.5rem] border border-slate-200 bg-navy p-8 text-white" data-testid="roadmap-cta">
                        <div className="flex items-center gap-2 text-teal-light"><MapPin className="h-5 w-5" /><span className="si-label !text-teal-light">The full plan</span></div>
                        <h3 className="mt-2 font-heading text-2xl font-bold">{roadmap.next_step || "Turn this roadmap into a done-with-you launch plan."}</h3>
                        {leadDone ? (
                            <div className="mt-5 flex items-start gap-3 rounded-xl bg-white/10 p-4" data-testid="lead-done">
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-light" />
                                <p className="text-sm text-slate-200">Thank you{lead.name ? `, ${lead.name}` : ""} — a Smarter Choice specialist will reach out to map your full plan. No payment is collected now; scope and pricing are shared before anything is signed.</p>
                            </div>
                        ) : (
                            <form onSubmit={submitLead} className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                                <div className="space-y-1.5"><Label htmlFor="lead-name" className="text-slate-200">Name</Label><Input id="lead-name" data-testid="lead-name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} required placeholder="Your name" className="bg-white text-navy" /></div>
                                <div className="space-y-1.5"><Label htmlFor="lead-email" className="text-slate-200">Email</Label><Input id="lead-email" data-testid="lead-email" type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} required placeholder="you@email.com" className="bg-white text-navy" /></div>
                                <div className="flex items-end"><Button type="submit" disabled={leadBusy} data-testid="lead-submit" className="h-11 w-full bg-teal font-bold text-navy hover:bg-teal-light sm:w-auto">{leadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get my full plan"}</Button></div>
                            </form>
                        )}
                    </div>

                    <p className="mt-6 text-xs leading-relaxed text-slate-500" data-testid="roadmap-disclaimer">{roadmap.disclaimer}</p>
                    <div className="mt-6"><Button variant="outline" onClick={() => { setRoadmap(null); setStep(0); }} data-testid="roadmap-restart" className="border-slate-300 text-slate-700 hover:bg-slate-100">Start over</Button></div>
                </div>
            </div>
        );
    }

    // ---------- Wizard view ----------
    const canNext = step === 0 ? form.vision.trim().length > 0 : true;
    const isLast = step === steps.length - 1;

    return (
        <div className="min-h-screen bg-pale" data-testid="strategizer-page">
            <TopBar navigate={navigate} />
            <div className="mx-auto max-w-2xl px-4 py-10 lg:px-8">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-ocean/10 px-4 py-1.5">
                    <Compass className="h-3.5 w-3.5 text-ocean" /><span className="text-xs font-bold uppercase tracking-wide text-ocean">Business Launch Strategizer</span>
                </div>
                <h1 className="font-heading text-3xl font-bold text-navy sm:text-4xl">Let's map your next move</h1>
                <p className="mt-3 text-slate-600">Answer a few quick questions and Smarter Intelligence will build you a free, step-by-step launch roadmap.</p>

                <div className="mt-6 flex gap-1.5" aria-hidden="true">
                    {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-ocean" : "bg-slate-200"}`} />)}
                </div>

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <div className="si-label mb-1 !text-slate-500">Step {step + 1} of {steps.length}</div>
                    <h2 className="mb-5 font-heading text-xl font-bold text-navy">{steps[step].title}</h2>
                    {steps[step].render}

                    <div className="mt-8 flex items-center justify-between">
                        <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="strat-back" className="text-slate-600 hover:bg-slate-100"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
                        {isLast ? (
                            <Button onClick={generate} disabled={busy} data-testid="strat-generate" className="bg-navy font-semibold text-white hover:bg-navy-light">
                                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building your roadmap…</> : <>Build my roadmap <Sparkles className="ml-2 h-4 w-4" /></>}
                            </Button>
                        ) : (
                            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="strat-next" className="bg-navy font-semibold text-white hover:bg-navy-light">Continue <ArrowRight className="ml-2 h-4 w-4" /></Button>
                        )}
                    </div>
                </div>
                <p className="mt-5 text-xs leading-relaxed text-slate-500">Educational guidance, not legal, tax, or investment advice. Entity, tax, and contract decisions should be confirmed with a licensed professional.</p>
            </div>
        </div>
    );
}

function TopBar({ navigate }) {
    return (
        <div className="border-b border-slate-200 bg-navy px-4 py-4 text-white lg:px-8">
            <div className="mx-auto flex max-w-4xl items-center justify-between">
                <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="strat-home-link"><Compass className="h-5 w-5 text-teal-light" /><span className="font-heading text-sm font-bold">Smarter Choice · Next Move</span></button>
                <Button size="sm" variant="ghost" onClick={() => navigate("/")} className="text-white hover:bg-white/10"><Home className="mr-1.5 h-4 w-4" /> Home</Button>
            </div>
        </div>
    );
}
